import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Check, Copy, Coffee, Info } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import { systemSettingsApi } from '../api/systemSettingsApi';

const WOCHENTAGE = [
  { key: 'montag', label: 'Montag' },
  { key: 'dienstag', label: 'Dienstag' },
  { key: 'mittwoch', label: 'Mittwoch' },
  { key: 'donnerstag', label: 'Donnerstag' },
  { key: 'freitag', label: 'Freitag' },
  { key: 'samstag', label: 'Samstag' },
  { key: 'sonntag', label: 'Sonntag' }
];

const DEFAULT_ZEITEN = {
  montag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
  dienstag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
  mittwoch: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
  donnerstag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
  freitag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
  samstag: { aktiv: false, von: '09:00', bis: '13:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
  sonntag: { aktiv: false, von: '09:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' }
};

function Geschaeftszeiten({ embedded = false }) {
  const navigate = useNavigate();
  const [zeiten, setZeiten] = useState(DEFAULT_ZEITEN);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  // Auto-Save: jede Änderung persistiert selbstständig — kein explicit-save-
  // Button mehr. Debounce verhindert, dass das Drehen am Zeit-Spinner pro
  // Zwischenwert einen API-Call auslöst.
  const saveTimerRef = useRef(null);
  const persist = (nextZeiten) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        await systemSettingsApi.updateOeffnungszeiten(nextZeiten);
        setSavedMessage('Geschäftszeiten gespeichert');
        setTimeout(() => setSavedMessage(''), 2500);
      } catch (error) {
        console.error('Error saving oeffnungszeiten:', error);
        setSavedMessage('Fehler beim Speichern');
      } finally {
        setSaving(false);
      }
    }, 400);
  };

  useEffect(() => {
    loadOeffnungszeiten();
  }, []);

  const loadOeffnungszeiten = async () => {
    try {
      setLoading(true);
      const data = await systemSettingsApi.getOeffnungszeiten();

      if (data.oeffnungszeiten) {
        // Bestehende Daten mit DEFAULT_ZEITEN mergen (fehlende Tage bekommen Defaults)
        const updated = { ...DEFAULT_ZEITEN };
        for (const key of Object.keys(data.oeffnungszeiten)) {
          updated[key] = {
            ...(DEFAULT_ZEITEN[key] || {}),
            ...data.oeffnungszeiten[key],
            mittagspause: data.oeffnungszeiten[key].mittagspause ?? false,
            pauseVon: data.oeffnungszeiten[key].pauseVon ?? '12:00',
            pauseBis: data.oeffnungszeiten[key].pauseBis ?? '13:00'
          };
        }
        setZeiten(updated);
      }
    } catch (error) {
      console.error('Error loading oeffnungszeiten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (tag) => {
    setZeiten(prev => {
      const next = { ...prev, [tag]: { ...prev[tag], aktiv: !prev[tag].aktiv } };
      persist(next);
      return next;
    });
  };

  const handleMittagspauseToggle = (tag) => {
    setZeiten(prev => {
      const next = { ...prev, [tag]: { ...prev[tag], mittagspause: !prev[tag].mittagspause } };
      persist(next);
      return next;
    });
  };

  const handleTimeChange = (tag, field, value) => {
    setZeiten(prev => {
      const next = { ...prev, [tag]: { ...prev[tag], [field]: value } };
      persist(next);
      return next;
    });
  };

  const copyToAll = (sourceTag) => {
    setZeiten(prev => {
      const sourceZeit = prev[sourceTag];
      const updated = { ...prev };
      WOCHENTAGE.forEach(({ key }) => {
        if (key !== 'samstag' && key !== 'sonntag') {
          updated[key] = { ...sourceZeit };
        }
      });
      persist(updated);
      return updated;
    });
  };

  const applyPreset = (preset) => {
    let next;
    switch (preset) {
      case 'standard':
        next = DEFAULT_ZEITEN;
        break;
      case 'mitPause':
        next = {
          montag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          dienstag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          mittwoch: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          donnerstag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          freitag: { aktiv: true, von: '08:00', bis: '17:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          samstag: { aktiv: false, von: '09:00', bis: '13:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          sonntag: { aktiv: false, von: '09:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' }
        };
        break;
      case 'vollzeit':
        next = {
          montag: { aktiv: true, von: '07:00', bis: '18:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          dienstag: { aktiv: true, von: '07:00', bis: '18:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          mittwoch: { aktiv: true, von: '07:00', bis: '18:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          donnerstag: { aktiv: true, von: '07:00', bis: '18:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          freitag: { aktiv: true, von: '07:00', bis: '18:00', mittagspause: true, pauseVon: '12:00', pauseBis: '13:00' },
          samstag: { aktiv: true, von: '08:00', bis: '14:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          sonntag: { aktiv: false, von: '09:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' }
        };
        break;
      case 'halbtags':
        next = {
          montag: { aktiv: true, von: '08:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          dienstag: { aktiv: true, von: '08:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          mittwoch: { aktiv: true, von: '08:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          donnerstag: { aktiv: true, von: '08:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          freitag: { aktiv: true, von: '08:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          samstag: { aktiv: false, von: '09:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' },
          sonntag: { aktiv: false, von: '09:00', bis: '12:00', mittagspause: false, pauseVon: '12:00', pauseBis: '13:00' }
        };
        break;
      default:
        return;
    }
    setZeiten(next);
    persist(next);
  };

  if (loading) {
    const spinner = (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
    if (embedded) return spinner;
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  const inner = (
    <>
      {!embedded && (
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/kalender')}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-serif font-bold">Geschäftszeiten</h1>
          </div>
        </div>
      )}

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-blue-800 text-sm">
            <p className="font-medium mb-1">Diese Zeiten werden verwendet für:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Kalender-Terminbuchungen</li>
              <li>KI-Agent Terminvorschläge an Kunden</li>
            </ul>
            <p className="mt-2 text-blue-600">Änderungen werden automatisch mit dem KI-Agenten synchronisiert.</p>
          </div>
        </div>

        {/* Vorlagen */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Schnellauswahl:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => applyPreset('standard')}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Standard (Mo-Fr 8-17)
            </button>
            <button
              onClick={() => applyPreset('mitPause')}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Mit Mittagspause
            </button>
            <button
              onClick={() => applyPreset('vollzeit')}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Vollzeit (inkl. Sa)
            </button>
            <button
              onClick={() => applyPreset('halbtags')}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Halbtags
            </button>
          </div>
        </div>

        {/* Zeiten-Liste */}
        <div className="bg-surface rounded-card shadow-card overflow-hidden">
          <div className="space-y-0">
            {WOCHENTAGE.map(({ key, label }) => (
              <div
                key={key}
                className={`p-4 border-b border-border last:border-b-0 transition ${
                  zeiten[key].aktiv
                    ? 'bg-white'
                    : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(key)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      zeiten[key].aktiv ? 'bg-primary' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        zeiten[key].aktiv ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>

                  {/* Tag */}
                  <span className={`w-28 font-medium flex-shrink-0 ${
                    zeiten[key].aktiv ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {label}
                  </span>

                  {/* Zeiten */}
                  {zeiten[key].aktiv ? (
                    <div className="flex items-center gap-2 flex-1 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <input
                          type="time"
                          value={zeiten[key].von}
                          onChange={(e) => handleTimeChange(key, 'von', e.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        />
                        <span className="text-gray-500">-</span>
                        <input
                          type="time"
                          value={zeiten[key].bis}
                          onChange={(e) => handleTimeChange(key, 'bis', e.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        />
                      </div>

                      {/* Mittagspause Toggle */}
                      <button
                        onClick={() => handleMittagspauseToggle(key)}
                        className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm transition ${
                          zeiten[key].mittagspause
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title="Mittagspause aktivieren/deaktivieren"
                      >
                        <Coffee className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Pause</span>
                      </button>

                      {/* Auf Werktage kopieren */}
                      {key !== 'samstag' && key !== 'sonntag' && (
                        <button
                          onClick={() => copyToAll(key)}
                          className="ml-auto text-gray-400 hover:text-primary transition flex-shrink-0"
                          title="Auf alle Werktage übertragen"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Geschlossen</span>
                  )}
                </div>

                {/* Mittagspause Zeiten */}
                {zeiten[key].aktiv && zeiten[key].mittagspause && (
                  <div className="mt-3 ml-[60px] sm:ml-[148px] flex items-center gap-2 text-sm">
                    <Coffee className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-600">Pause:</span>
                    <input
                      type="time"
                      value={zeiten[key].pauseVon}
                      onChange={(e) => handleTimeChange(key, 'pauseVon', e.target.value)}
                      className="px-2 py-1 border border-orange-200 bg-orange-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                    />
                    <span className="text-gray-500">-</span>
                    <input
                      type="time"
                      value={zeiten[key].pauseBis}
                      onChange={(e) => handleTimeChange(key, 'pauseBis', e.target.value)}
                      className="px-2 py-1 border border-orange-200 bg-orange-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tipp */}
        <div className="mt-4 bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
          <strong>Tipp:</strong> Klicken Sie auf "Pause", um eine Mittagspause für den jeweiligen Tag zu aktivieren.
          Mit dem <Copy className="w-3.5 h-3.5 inline" />-Symbol übertragen Sie die Zeiten auf alle Werktage.
        </div>

        {/* Auto-Save Status */}
        {(saving || savedMessage) && (
          <div className="mt-4 flex items-center gap-2 text-sm text-text-muted">
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>Speichern…</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-green-600">{savedMessage}</span>
              </>
            )}
          </div>
        )}
    </>
  );

  if (embedded) {
    return <div className="space-y-4">{inner}</div>;
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {inner}
      </div>
    </Layout>
  );
}

export default Geschaeftszeiten;
